import { useCallback, useState } from "react";
import { Card, Gate, Head } from "@authoxi/ui-shared";
import { verifyEvent } from "@authoxi/js/verify";
import { client } from "../api";

/**
 * Check our work without us.
 *
 * This runs the **shipped verifier** — the same `@authoxi/js` a sceptic installs, held to one
 * cross-language known-answer vector against the Python signer in CI. Not a re-implementation:
 * a verifier with its own private copy of the rules would eventually disagree with the one
 * people actually run, and it would disagree in the flattering direction.
 *
 * That is not hypothetical. This exact check is what caught the browser verifier reporting
 * **genuine, human-approved decisions as FORGED** — four post-v2 covariates are dropped from a
 * signing body when unset, Python knew and the TypeScript did not, and the `/receipt` page was
 * telling recipients that real decisions were forgeries.
 *
 * Nothing is uploaded. The event is read in this tab and verified in it.
 */
export function Verify() {
  const [result, setResult] = useState<Awaited<ReturnType<typeof verifyEvent>> | null>(null);
  const [raw, setRaw] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const check = useCallback(async (text: string) => {
    setError(null);
    setRaw(text);
    try {
      setResult(await verifyEvent(JSON.parse(text)));
    } catch (e) {
      setResult(null);
      setError(e instanceof SyntaxError ? "That is not JSON." : String(e));
    }
  }, []);

  return (
    <>
      <Head
        pill="Offline verifier"
        title="Check our work without us."
        lede="Drop a signed event. It is verified here, in your browser, against the key inside the event itself — nothing is uploaded. The same check runs as a CLI with zero dependencies, so a sceptic never has to take our word for anything."
      />

      <label
        className="drop"
        data-testid="drop"
        onDragOver={(e) => e.preventDefault()}
        onDrop={async (e) => {
          e.preventDefault();
          const file = e.dataTransfer.files[0];
          if (file) void check(await file.text());
        }}
      >
        <p className="md" style={{ margin: 0 }}>
          Drop a signed event
        </p>
        <p className="sm muted" style={{ margin: "var(--s2) 0 0" }}>
          or choose a file · nothing leaves this page
        </p>
        <input
          type="file"
          accept="application/json,.json"
          style={{ display: "none" }}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (file) void check(await file.text());
          }}
        />
      </label>

      <p className="sm muted" style={{ marginTop: "var(--s4)" }}>
        Or paste one:
      </p>
      <textarea
        className="input input-mono"
        data-testid="verify-input"
        rows={6}
        placeholder='{"schema_version": "loss-event/v2", …}'
        value={raw}
        onChange={(e) => void check(e.target.value)}
        style={{ width: "100%", marginTop: "var(--s2)" }}
      />

      {error && (
        <p className="sm" style={{ color: "var(--deny-ink)", marginTop: "var(--s3)" }}>
          {error}
        </p>
      )}

      {result && (
        <div className="result" data-testid="verify-result" style={{ marginTop: "var(--s5)" }}>
          <Card pad={false}>
            <div className={`receipt-foot${result.ok ? "" : " bad"}`}>
              <strong className="mono" style={{ fontSize: "var(--fs-body)" }} data-testid="verify-verdict">
                {result.ok ? "✓ VERIFIED" : "✗ FORGERY"}
              </strong>
              <span className="xs" style={{ opacity: 0.85, flex: 1, minWidth: 200 }}>
                {result.ok
                  ? "Every signature in this event checks out against the keys named inside it."
                  : "A byte changed after signing. The signature covers all of them, so any edit breaks it."}
              </span>
            </div>
            <dl style={{ margin: 0 }}>
              {result.checks.map((c) => (
                <div className="receipt-row" key={c.name}>
                  <dt>{c.name}</dt>
                  <dd>
                    {c.ok ? "✓" : "✗"} {c.detail}
                  </dd>
                </div>
              ))}
            </dl>
          </Card>
          {result.ok && (
            <Gate
              client={client}
              source="verify"
              title="Verify at scale"
              body="Checking one event by hand is the demo. Checking every event your fleet produces, continuously, is the product."
              cta="Show me"
            />
          )}
        </div>
      )}

      <Card pad="lg" className="">
        <p className="label">Same check, on your machine</p>
        <pre className="code" style={{ marginTop: "var(--s3)" }}>{`npx -p @authoxi/js authoxi-verify event.json

# ✓ emitter signature valid
# ✓ approver signature valid — ops@acme.com
# witness: self — not a proof of independent review`}</pre>
        <p className="sm muted" style={{ marginTop: "var(--s4)" }}>
          Three implementations — Python, WebCrypto and node:crypto — are held to one
          cross-language vector in CI. That check is not decoration: it is what caught the
          browser verifier reporting genuine decisions as forgeries.
        </p>
      </Card>
    </>
  );
}
